import { useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { CalendarDays, Gem, Gift, HeartHandshake, ShieldCheck, Ticket } from "lucide-react";
import { GameModal } from "../../components/GameModal";
import { PublicLayout } from "../../components/PublicLayout";
import { BannerHero } from "../../components/public/BannerHero";
import { CalculatorWidget } from "../../components/public/CalculatorWidget";
import { LobbyShortcutGrid } from "../../components/public/LobbyShortcutGrid";
import { MainQuestSummary } from "../../components/public/MainQuestSummary";
import { MobileDrawer } from "../../components/public/MobileDrawer";
import { PlayerStatusBar } from "../../components/public/PlayerStatusBar";
import { api } from "../../lib/api";
import { bannerConfigs } from "../../lib/bannerConfig";
import { isLocalDevFrontend } from "../../lib/env";
import {
  clearStoredLobbyMemberIdentity,
  readStoredMemberAccount,
  readStoredMemberIdentityCode,
  writeStoredLobbyMemberIdentity,
} from "../../lib/memberIdentityStorage";

interface RewardRef { id?: number; reward_type?: string; reward_status: string; amount?: number | string; created_at?: string | null; claimed_at?: string | null; }
interface VoucherDetail { voucher_type: string; status: string; count: number; }
interface MemberSummary {
  is_bound: boolean;
  has_activation_deposit: boolean;
  member: null | {
    member_account: string;
    identity_code: string;
    status: string;
    status_label: string;
    graduation_at?: string | null;
  };
  activation_deposit?: { amount?: number | string } | null;
  step1_reward?: null | RewardRef;
  step2_reward?: null | RewardRef;
  step3_reward?: null | RewardRef;
  step4_reward?: null | RewardRef;
  step5_reward?: null | RewardRef;
  step6_reward?: null | RewardRef;
  step7_final10_reward?: null | RewardRef;
  step7_graduation_reward?: null | RewardRef;
  regular_rebate_rewards?: RewardRef[];
  newbie_progress?: null | {
    invite_unlocked_at?: string | null;
    step4_status?: string | null;
    step5_status?: string | null;
    step6_status?: string | null;
    step7_status?: string | null;
  };
  invite_status?: null | { is_unlocked: boolean; unlocked_at?: string | null };
  assets: {
    vouchers: number;
    voucher_details?: VoucherDetail[];
    gems: number;
    weekly_pass_points: number;
    glory_value: number;
  };
}

interface GraduateWeekTask {
  task_code: "checkin" | "single1000" | "total5000";
  title: string;
  reward_text: string;
  status: "completed" | "available" | "locked" | "not_eligible";
  completed_at?: string | null;
  deposit_amount?: number | null;
  cumulative_amount?: number | null;
}

interface GraduateWeekStatus {
  is_graduated: boolean;
  is_open: boolean;
  is_expired: boolean;
  remaining_text?: string;
  message?: string;
  tasks: GraduateWeekTask[];
}

interface WeeklyPassTask {
  task_code: string;
  title: string;
  status: "completed" | "available" | "not_eligible" | "locked";
  status_label: string;
}

interface WeeklyPassStatus {
  is_graduated: boolean;
  is_available?: boolean;
  locked_reason?: string | null;
  graduate_week_end_at?: string | null;
  weekly_pass_available_at?: string | null;
  remaining_seconds?: number;
  display_message?: string;
  week_id: string;
  week_start_at: string;
  week_end_at: string;
  remaining_text: string;
  points: number;
  max_points: number;
  tasks: WeeklyPassTask[];
  next_task?: WeeklyPassTask | null;
  reward_summary?: string[];
}

interface WeekendBonusStatus {
  is_available: boolean;
  locked_reason?: string | null;
  display_message: string;
  weekend_start_at: string;
  weekend_end_at: string;
  next_weekend_start_at: string;
  remaining_seconds: number;
  tasks?: { task_code: string; status: string; title: string }[];
}

const loginBenefits = [
  { title: "首存 100%", value: "70% 立即領", text: "入盟啟動後，首日主獎勵進入審核流程，後續 30% 依任務解鎖。" },
  { title: "續存加碼", value: "二存 80% / 三存 50%", text: "完成指定存款任務後，由客服確認入帳。" },
  { title: "新人紅包", value: "最高 58,888", text: "完成指定關卡可使用新人啟航紅包券抽取河幣結果。" },
  { title: "回穩補給金", value: "審核制", text: "正式成員可依活動狀況送出申請，由客服審核確認。" }
];

export function LobbyPage() {
  const isLocalDev = isLocalDevFrontend();
  const savedAccount = readStoredMemberAccount();
  const savedIdentityCode = readStoredMemberIdentityCode();
  const [memberAccount, setMemberAccount] = useState(savedAccount || (isLocalDev ? "test003" : ""));
  const [phoneLast3, setPhoneLast3] = useState(savedIdentityCode || (savedAccount ? savedAccount.slice(-3) : (isLocalDev ? "003" : "")));
  const [summary, setSummary] = useState<MemberSummary | null>(null);
  const [graduateWeek, setGraduateWeek] = useState<GraduateWeekStatus | null>(null);
  const [weeklyPass, setWeeklyPass] = useState<WeeklyPassStatus | null>(null);
  const [weekendBonus, setWeekendBonus] = useState<WeekendBonusStatus | null>(null);
  const [modal, setModal] = useState<{ title: string; text: string; tone?: "info" | "success" | "error" | "reward" } | null>(null);
  const [loading, setLoading] = useState(false);

  const loggedIn = Boolean(summary?.member);
  const inviteUnlocked = Boolean(summary?.invite_status?.is_unlocked || summary?.newbie_progress?.invite_unlocked_at);
  const isGraduated = Boolean(summary?.member?.status === "graduated" || summary?.newbie_progress?.step7_status === "completed");
  const quest = useMemo(() => getQuestState(summary), [summary]);
  const nextStepState = useMemo(() => getLobbyNextStep(summary, weeklyPass, graduateWeek, quest), [summary, weeklyPass, graduateWeek, quest]);
  const hideNextStepAction = isGraduated && nextStepState.href === "/weekly-pass";

  async function loadSummary(account = memberAccount) {
    if (!account.trim()) return;
    const nextSummary = await api<MemberSummary>(`/api/member/summary?member_account=${encodeURIComponent(account.trim())}`);
    setSummary(nextSummary);
    if (nextSummary.member?.status === "graduated" || nextSummary.newbie_progress?.step7_status === "completed") {
      try {
        setGraduateWeek(await api<GraduateWeekStatus>(`/api/public/graduate-week?member_account=${encodeURIComponent(account.trim())}`));
      } catch {
        setGraduateWeek(null);
      }
      try {
        setWeeklyPass(await api<WeeklyPassStatus>(`/api/public/weekly-pass?member_account=${encodeURIComponent(account.trim())}`));
      } catch {
        setWeeklyPass(null);
      }
      try {
        setWeekendBonus(await api<WeekendBonusStatus>(`/api/public/weekend-bonus?member_account=${encodeURIComponent(account.trim())}`));
      } catch {
        setWeekendBonus(null);
      }
    } else {
      setGraduateWeek(null);
      setWeeklyPass(null);
      setWeekendBonus(null);
    }
  }

  async function loginOrBind() {
    setLoading(true);
    try {
      const account = memberAccount.trim();
      const result = await api<{ created: boolean; message: string }>("/api/bind-member", {
        method: "POST",
        body: JSON.stringify({ member_account: account, identity_code: phoneLast3.trim() })
      });
      writeStoredLobbyMemberIdentity(account, phoneLast3);
      await loadSummary(account);
      setModal({
        title: result.created ? "綁定成功" : "登入成功",
        text: result.created ? "已建立會員資料，請等待後台渠道確認。" : "已載入你的活動進度。",
        tone: "success"
      });
    } catch (err) {
      setModal({ title: "登入失敗", text: err instanceof Error ? err.message : "請稍後再試。", tone: "error" });
    } finally {
      setLoading(false);
    }
  }

  function logout() {
    clearStoredLobbyMemberIdentity();
    setSummary(null);
    setModal({ title: "已切換會員", text: "請重新輸入會員帳號與手機後 3 碼。", tone: "info" });
  }

  useEffect(() => {
    if (savedAccount) loadSummary(savedAccount).catch(() => undefined);
  }, []);

  return (
    <PublicLayout title="龍來喬活動大廳" subtitle={loggedIn ? "會員活動總覽" : "登入 / 綁定會員"}>
      {!loggedIn ? (
        <main className="login-page">
          <BannerHero banner={bannerConfigs.login}>
            <div className="hero-login-form">
              <p>輸入會員帳號與手機後 3 碼，進入龍來喬活動大廳查看任務、紅包券與會員權益。</p>
              <label>會員帳號<input value={memberAccount} onChange={(event) => setMemberAccount(event.target.value)} placeholder={isLocalDev ? "test003" : "請輸入會員帳號"} /></label>
              <label>手機後 3 碼<input value={phoneLast3} onChange={(event) => setPhoneLast3(event.target.value)} placeholder={isLocalDev ? "003" : "手機後 3 碼"} maxLength={3} /></label>
              <button className="primary-cta" onClick={loginOrBind} disabled={loading}>{loading ? "登入中..." : "登入龍來喬"}</button>
              <a className="secondary-cta" href="#calculator-widget">試算首存加碼</a>
            </div>
          </BannerHero>
          <section className="login-benefit-grid" aria-label="活動亮點">
            {loginBenefits.map((item) => (
              <article className="login-benefit-card" key={item.title}>
                <span>{item.title}</span><strong>{item.value}</strong><p>{item.text}</p>
              </article>
            ))}
          </section>
          <CalculatorWidget title="首存加碼試算" compact />
        </main>
      ) : (
        <main className="mobile-lobby">
          <div className="lobby-header-bar">
            <MobileDrawer onLogout={logout} />
            <span>目前會員：{summary?.member?.member_account}</span>
            <button className="secondary account-switch" type="button" onClick={logout}>切換會員</button>
          </div>
          <BannerHero banner={bannerConfigs.lobby} className="compact-lobby-hero">
            <a className="secondary-cta" href="/calculator">{isGraduated ? "試算常態回饋" : "試算加碼"}</a>
          </BannerHero>
          <section className="next-step-card" id="next-step">
            <span className="hero-kicker">下一步</span>
            <h2>{nextStepState.title}</h2>
            <p>{nextStepState.text}</p>
            {nextStepState.meta ? <strong>{nextStepState.meta}</strong> : null}
            {nextStepState.rewardText ? <p className="graduate-week-hint">{nextStepState.rewardText}</p> : null}
            {!hideNextStepAction ? <a className={nextStepState.primary ? "primary-cta" : "secondary-cta"} href={nextStepState.href}>{nextStepState.action}</a> : null}
          </section>
          <PlayerStatusBar
            identity={summary?.member?.status_label ?? "會員"}
            status={summary?.member?.status_label ?? "會員"}
            gems={summary?.assets.gems ?? 0}
            vouchers={summary?.assets.vouchers ?? 0}
            gloryValue={summary?.assets.glory_value ?? 0}
            weeklyPassPoints={summary?.assets.weekly_pass_points ?? 0}
            inviteUnlocked={inviteUnlocked}
          />
          {isGraduated ? (
            <GraduatedActivityPanelV3 summary={summary} graduateWeek={graduateWeek} weeklyPass={weeklyPass} weekendBonus={weekendBonus} />
          ) : (
            <MainQuestSummary
              currentProgress={quest.progress}
              nextStep={quest.nextStep}
              activationDepositAmount={summary?.activation_deposit?.amount ?? null}
              inviteUnlocked={inviteUnlocked}
              gems={summary?.assets.gems ?? 0}
              completedCount={quest.completedCount}
              extraUnlocked={quest.extraUnlocked}
            />
          )}
          {!isGraduated ? <LobbyShortcutGrid vouchers={summary?.assets.vouchers ?? 0} gems={summary?.assets.gems ?? 0} /> : null}
          <section className="vip-join-card">
            <span className="hero-kicker">會員身份</span>
            <h2>會員身份 / 公會等級</h2>
            <p>查看目前閃耀值、會員身份與公會等級權益。</p>
            <a className="secondary-cta" href="/level">查看公會等級</a>
          </section>
        </main>
      )}
      <GameModal open={Boolean(modal)} title={modal?.title ?? ""} tone={modal?.tone ?? "info"} onClose={() => setModal(null)}>
        <p>{modal?.text}</p>
      </GameModal>
    </PublicLayout>
  );
}

function GraduatedActivityPanelV3({
  summary,
  graduateWeek,
  weeklyPass,
  weekendBonus
}: {
  summary: MemberSummary | null;
  graduateWeek: GraduateWeekStatus | null;
  weeklyPass: WeeklyPassStatus | null;
  weekendBonus: WeekendBonusStatus | null;
}) {
  const voucherDetails = summary?.assets.voucher_details ?? [];
  const newbieBonusCount = countVoucher(voucherDetails, "newbie_bonus_red_packet", "available");
  const gems = summary?.assets.gems ?? 0;
  const graduateWindow = graduateWeek?.remaining_text || graduateWeek?.message || getGraduateWeekWindow(summary?.member?.graduation_at);
  const graduateTasks = graduateWeek?.tasks?.length ? graduateWeek.tasks : [];
  const completedGraduateTasks = graduateTasks.filter((task) => task.status === "completed").length;
  const nextGraduateTask = graduateTasks.find((task) => task.status !== "completed");
  const graduateWeekComplete = graduateTasks.length >= 3 && completedGraduateTasks >= 3;
  const graduateWeekEnded = Boolean(graduateWeek?.is_expired);
  const showGraduateWeekAsActive = !graduateWeekComplete && !graduateWeekEnded;
  const latestRegularRebate = summary?.regular_rebate_rewards?.[0] ?? null;
  const weeklyPassAvailable = weeklyPass?.is_available !== false;
  const weeklyPoints = weeklyPass?.points ?? summary?.assets.weekly_pass_points ?? 0;
  const weeklyMax = weeklyPass?.max_points ?? 5;
  const weeklyPassComplete = weeklyPassAvailable && weeklyPoints >= weeklyMax;
  const weekendText = weekendBonus?.is_available
    ? "週末耀旺開放中，可完成週末任務取得週末紅包券。"
    : weekendBonus?.display_message ?? "週末耀旺將於週末期間開放。";
  const weeklyNextTask = weeklyPass?.next_task?.title ?? (weeklyPoints < weeklyMax ? "每週打卡 1 次" : "本週任務已完成");
  const weeklyRewards = weeklyPass?.reward_summary?.length ? weeklyPass.reward_summary.join("、") : "尚未獲得本週獎勵";

  return (
    <section className="graduate-activity-panel" id="graduate-activities">
      {!weeklyPassAvailable ? (
        !graduateWeekEnded ? (
          <article className="graduate-week-card graduate-week-secondary-card" id="graduate-week">
            <span className="graduate-card-icon"><CalendarDays size={24} /></span>
            <div>
              <span className="hero-kicker">限時加碼活動</span>
              <h2>{graduateWeekComplete ? "畢業首週加碼已完成" : "畢業首週啟航禮"}</h2>
              <p>{graduateWeekComplete ? "你已完成畢業首週主要任務，獎勵已發放。週通行證將於畢業首週結束後的下一個週一開啟。" : "你已完成新人七關，畢業首週限時加碼活動已開啟。完成打卡與充值任務，可獲得寶石碎片與啟航紅包券。"}</p>
              <strong>{graduateWindow}</strong>
              <strong>已完成獎勵：{completedGraduateTasks} / 3</strong>
              <p className="graduate-week-hint">{weeklyPass?.weekly_pass_available_at ? `週通行證開啟：${formatDateTime(weeklyPass.weekly_pass_available_at)}` : getGraduateNextGoalText(nextGraduateTask)}</p>
            </div>
            <a className="primary-cta" href="/graduate-week">查看畢業首週{graduateWeekComplete ? "紀錄" : ""}</a>
          </article>
        ) : (
          <article className="graduate-week-card weekly-pass-main-card" id="weekly-pass">
            <span className="graduate-card-icon"><Ticket size={24} /></span>
            <div>
              <span className="hero-kicker">目前活動狀態</span>
              <h2>週通行證即將開啟</h2>
              <p>畢業首週已結束，週通行證將於下一個週一 00:00 開啟。屆時每週完成任務，可領取寶石碎片、週耀紅包券與閃耀值。</p>
              {weeklyPass?.weekly_pass_available_at ? <strong>開啟時間：{formatDateTime(weeklyPass.weekly_pass_available_at)}</strong> : null}
              <strong>{formatRemainingSeconds(weeklyPass?.remaining_seconds)}</strong>
            </div>
            <a className="secondary-cta" href="#completed-activities">查看活動紀錄</a>
          </article>
        )
      ) : weeklyPassComplete ? (
        <article className="graduate-week-card weekly-pass-main-card" id="weekly-pass">
          <span className="graduate-card-icon"><Ticket size={24} /></span>
          <div>
            <span className="hero-kicker">目前活動狀態</span>
            <h2>本週主要任務已完成</h2>
            <p>你已完成本週週通行證任務，獎勵已發放。可查看其他活動或等待下週任務開啟。</p>
            <strong>本週點數：{weeklyPoints} / {weeklyMax}</strong>
            <strong>{weeklyPass?.remaining_text ?? "本週期間同步中"}</strong>
            <p className="graduate-week-hint">已獲得：{weeklyRewards}</p>
          </div>
          <a className="secondary-cta" href="/weekly-pass">查看週通行證紀錄</a>
        </article>
      ) : (
        <article className="graduate-week-card weekly-pass-main-card" id="weekly-pass">
          <span className="graduate-card-icon"><Ticket size={24} /></span>
          <div>
            <span className="hero-kicker">週通行證</span>
            <h2>週通行證</h2>
            <p>本週完成任務可累積通行證點數，領取寶石碎片、週耀紅包券與閃耀值。</p>
            <strong>本週點數：{weeklyPoints} / {weeklyMax}</strong>
            <strong>{weeklyPass?.remaining_text ?? "本週期間同步中"}</strong>
            <p className="graduate-week-hint">下一個任務：{weeklyNextTask}</p>
            <p className="graduate-week-hint">已獲得：{weeklyRewards}</p>
          </div>
          <a className="primary-cta" href="/weekly-pass">查看週通行證</a>
        </article>
      )}

      {weeklyPassAvailable && showGraduateWeekAsActive ? (
        <article className="graduate-week-card graduate-week-secondary-card" id="graduate-week">
          <span className="graduate-card-icon"><CalendarDays size={24} /></span>
          <div>
            <span className="hero-kicker">限時加碼活動</span>
            <h2>畢業首週啟航禮</h2>
            <p>畢業首週為限時加碼活動，完成越多任務可領越多獎勵。</p>
            <strong>{graduateWindow}</strong>
            <strong>已完成獎勵：{completedGraduateTasks} / 3</strong>
            <p className="graduate-week-hint">{getGraduateNextGoalText(nextGraduateTask)}</p>
          </div>
          <a className="secondary-cta" href="/graduate-week">查看畢業首週</a>
        </article>
      ) : null}

      {weeklyPassAvailable && showGraduateWeekAsActive && graduateTasks.length ? (
        <div className="graduate-week-summary-list" id="graduate-week-tasks">
          {graduateTasks.map((task) => (
            <div className={`graduate-week-summary-item ${task.status}`} key={task.task_code}>
              <span>{graduateTaskStatusLabel(task.status)}</span>
              <strong>{task.title}</strong>
            </div>
          ))}
        </div>
      ) : null}

      <div className="admin-card-head">
        <span>更多活動</span>
        <h2>畢業後活動入口</h2>
      </div>
      <div className="graduate-activity-grid">
        <GraduateCard icon={<Gift size={22} />} title="新手加碼紅包" text={newbieBonusCount > 0 ? `你有新手加碼紅包券 ${newbieBonusCount} 張，可前往抽紅包。` : "目前沒有可用的新手加碼紅包券。"} href="/red-packet" action="前往紅包" />
        <GraduateCard icon={<Gem size={22} />} title="寶石兌換" text={gems >= 3 ? `目前寶石碎片 ${gems} 枚，可兌換新手加碼紅包券。` : `目前寶石碎片 ${gems} 枚，完成任務可取得更多寶石碎片。`} href="/gems" action="前往兌換" />
        <GraduateCard icon={<ShieldCheck size={22} />} title="邀友支線" text="邀友支線已開啟，可查看邀友進度與獎勵紀錄。" href="/invite" action="前往邀友" />
        <GraduateCard icon={<CalendarDays size={22} />} title="週末耀旺" text={weekendText} href="/weekend-bonus" action="查看週末耀旺" />
        <GraduateCard icon={<HeartHandshake size={22} />} title="回穩補給金" text="正式成員可依活動狀況申請回穩補給金，由客服審核確認。" href="/rescue" action="查看補給金" />
      </div>

      <div className="completed-activity-records" id="completed-activities">
        <div className="admin-card-head">
          <span>已完成活動紀錄</span>
          <h2>活動紀錄</h2>
        </div>
        {latestRegularRebate ? (
          <article className="completed-activity-card">
            <span className="graduate-card-icon"><Gift size={22} /></span>
            <div>
              <h3>正式成員常態回饋</h3>
              <strong>{regularRebateStatusText(latestRegularRebate)}｜{formatCoinAmount(latestRegularRebate.amount)} 河幣</strong>
              <p>{latestRegularRebate.reward_status === "completed" ? "你的常態回饋已由官方審核並完成入帳。" : "你的常態回饋已建立，等待客服確認後入帳。"}</p>
            </div>
            <a className="secondary-cta" href="#completed-activities">{latestRegularRebate.reward_status === "completed" ? "查看派發紀錄" : "查看派發狀態"}</a>
          </article>
        ) : null}
        {weeklyPassComplete ? (
          <article className="completed-activity-card">
            <span className="graduate-card-icon"><Ticket size={22} /></span>
            <div>
              <h3>週通行證</h3>
              <strong>已完成 {weeklyPoints}/{weeklyMax}</strong>
              <p>本週通行證任務已完成，寶石碎片、週耀紅包券與閃耀值獎勵已發放。</p>
            </div>
            <a className="secondary-cta" href="/weekly-pass">查看週通行證紀錄</a>
          </article>
        ) : null}
        {(!showGraduateWeekAsActive || !weeklyPassAvailable) ? (
          <article className="completed-activity-card">
            <span className="graduate-card-icon"><CalendarDays size={22} /></span>
            <div>
              <h3>畢業首週啟航禮</h3>
              <strong>{graduateWeekComplete ? "已完成 3/3" : "活動已結束"}</strong>
              <p>{graduateWeekComplete ? "畢業首週限時加碼活動已完成。" : "畢業首週限時加碼活動已結束，可查看紀錄。"}</p>
            </div>
            <a className="secondary-cta" href="/graduate-week">查看畢業首週紀錄</a>
          </article>
        ) : null}
        <article className="completed-activity-card">
          <span className="graduate-card-icon"><ShieldCheck size={22} /></span>
          <div>
            <h3>新人七關已完成</h3>
            <strong>7/7</strong>
            <p>你已完成新人七關，正式成為龍來喬成員。此紀錄保留在關卡頁中，可隨時查看。</p>
          </div>
          <a className="secondary-cta" href="/newbie">查看新人七關紀錄</a>
        </article>
      </div>
    </section>
  );
}

function GraduatedActivityPanelV2({
  summary,
  graduateWeek,
  weeklyPass
}: {
  summary: MemberSummary | null;
  graduateWeek: GraduateWeekStatus | null;
  weeklyPass: WeeklyPassStatus | null;
}) {
  const voucherDetails = summary?.assets.voucher_details ?? [];
  const newbieBonusCount = countVoucher(voucherDetails, "newbie_bonus_red_packet", "available");
  const gems = summary?.assets.gems ?? 0;
  const graduateWindow = graduateWeek?.remaining_text || graduateWeek?.message || getGraduateWeekWindow(summary?.member?.graduation_at);
  const graduateTasks = graduateWeek?.tasks?.length ? graduateWeek.tasks : [];
  const completedGraduateTasks = graduateTasks.filter((task) => task.status === "completed").length;
  const nextGraduateTask = graduateTasks.find((task) => task.status !== "completed");
  const weeklyPoints = weeklyPass?.points ?? summary?.assets.weekly_pass_points ?? 0;
  const weeklyMax = weeklyPass?.max_points ?? 5;
  const weeklyNextTask = weeklyPass?.next_task?.title ?? "本週任務已完成";
  const weeklyRewards = weeklyPass?.reward_summary?.length ? weeklyPass.reward_summary.join("、") : "尚未獲得本週獎勵";

  return (
    <section className="graduate-activity-panel" id="graduate-activities">
      <article className="graduate-week-card weekly-pass-main-card" id="weekly-pass">
        <span className="graduate-card-icon"><Ticket size={24} /></span>
        <div>
          <span className="hero-kicker">週通行證</span>
          <h2>週通行證</h2>
          <p>本週完成任務可累積通行證點數，領取寶石碎片、週耀紅包券與閃耀值。</p>
          <strong>本週點數：{weeklyPoints} / {weeklyMax}</strong>
          <strong>{weeklyPass?.remaining_text ?? "本週期間讀取中"}</strong>
          <p className="graduate-week-hint">下一個任務：{weeklyNextTask}</p>
          <p className="graduate-week-hint">已獲得：{weeklyRewards}</p>
        </div>
        <a className="primary-cta" href="/weekly-pass">查看週通行證</a>
      </article>

      <article className="graduate-week-card graduate-week-secondary-card" id="graduate-week">
        <span className="graduate-card-icon"><CalendarDays size={24} /></span>
        <div>
          <span className="hero-kicker">限時加碼活動</span>
          <h2>畢業首週啟航禮</h2>
          <p>畢業首週限時加碼活動，完成越多任務可領越多獎勵。</p>
          <strong>{graduateWindow}</strong>
          <strong>已完成獎勵：{completedGraduateTasks} / 3</strong>
          <p className="graduate-week-hint">{getGraduateNextGoalText(nextGraduateTask)}</p>
        </div>
        <a className="secondary-cta" href="/graduate-week">查看畢業首週</a>
      </article>

      {graduateTasks.length ? (
        <div className="graduate-week-summary-list" id="graduate-week-tasks">
          {graduateTasks.map((task) => (
            <div className={`graduate-week-summary-item ${task.status}`} key={task.task_code}>
              <span>{graduateTaskStatusLabel(task.status)}</span>
              <strong>{task.title}</strong>
            </div>
          ))}
        </div>
      ) : null}

      <div className="admin-card-head">
        <span>畢業後入口</span>
        <h2>更多活動</h2>
      </div>
      <div className="graduate-activity-grid">
        <GraduateCard icon={<Gift size={22} />} title="新手加碼紅包" text={newbieBonusCount > 0 ? `你有新手加碼紅包券 ${newbieBonusCount} 張，可前往抽紅包。` : "目前沒有可用的新手加碼紅包券。"} href="/red-packet" action="前往紅包" />
        <GraduateCard icon={<Gem size={22} />} title="寶石兌換" text={gems >= 3 ? `目前寶石碎片 ${gems} 枚，可兌換新手加碼紅包券。` : `目前寶石碎片 ${gems} 枚，完成任務可取得更多寶石。`} href="/gems" action="前往兌換" />
        <GraduateCard icon={<ShieldCheck size={22} />} title="邀友支線" text="邀友支線已開啟，可查看邀友進度與首存獎勵紀錄。" href="/invite" action="查看邀友" />
        <GraduateCard icon={<HeartHandshake size={22} />} title="回穩補給金" text="正式成員可依本月狀況送出回穩補給金申請，由後台人工審核。" href="/rescue" action="查看補給金" />
      </div>
    </section>
  );
}

function GraduatedActivityPanel({ summary, graduateWeek }: { summary: MemberSummary | null; graduateWeek: GraduateWeekStatus | null }) {
  const voucherDetails = summary?.assets.voucher_details ?? [];
  const newbieBonusCount = countVoucher(voucherDetails, "newbie_bonus_red_packet", "available");
  const gems = summary?.assets.gems ?? 0;
  const graduateWindow = graduateWeek?.remaining_text || graduateWeek?.message || getGraduateWeekWindow(summary?.member?.graduation_at);
  const tasks = graduateWeek?.tasks?.length ? graduateWeek.tasks : [
    { task_code: "checkin" as const, title: "畢業打卡", reward_text: "寶石碎片 +1", status: "locked" as const },
    { task_code: "single1000" as const, title: "畢業首週單筆充值滿 1,000", reward_text: "畢業啟航紅包券 1 張", status: "locked" as const },
    { task_code: "total5000" as const, title: "畢業首週累積充值滿 5,000", reward_text: "高級啟航紅包券 1 張", status: "locked" as const }
  ];
  const completedTasks = tasks.filter((task) => task.status === "completed").length;
  const nextTask = tasks.find((task) => task.status !== "completed");
  const nextTaskText = getGraduateNextGoalText(nextTask);

  return (
    <section className="graduate-activity-panel" id="graduate-activities">
      <article className="graduate-week-card" id="graduate-week">
        <span className="graduate-card-icon"><CalendarDays size={24} /></span>
        <div>
          <span className="hero-kicker">畢業首週</span>
          <h2>畢業首週啟航禮</h2>
          <p>你已完成新人七關，畢業首週活動已開啟。畢業首週為限時加碼活動，完成越多任務可領越多獎勵。</p>
          <strong>{graduateWindow}</strong>
          <strong>已完成獎勵：{completedTasks} / 3</strong>
          <p className="graduate-week-hint">{nextTaskText}</p>
        </div>
        <a className="primary-cta" href="/graduate-week">查看畢業首週</a>
      </article>
      <div className="graduate-week-summary-list" id="graduate-week-tasks">
        {tasks.map((task) => (
          <div className={`graduate-week-summary-item ${task.status}`} key={task.task_code}>
            <span>{graduateTaskStatusLabel(task.status)}</span>
            <strong>{task.title}</strong>
          </div>
        ))}
      </div>
      <div className="admin-card-head">
        <span>更多活動</span>
        <h2>畢業後活動入口</h2>
      </div>
      <div className="graduate-activity-grid">
        <GraduateCard icon={<Gift size={22} />} title="新手加碼紅包" text={newbieBonusCount > 0 ? `你有新手加碼紅包券 ${newbieBonusCount} 張，抽獎功能即將開放。` : "目前沒有可用的新手加碼紅包券。"} href="/red-packet" action="查看紅包" />
        <GraduateCard icon={<Gem size={22} />} title="寶石兌換" text={gems >= 3 ? `目前寶石碎片 ${gems} 枚，可兌換新手加碼紅包券。` : `目前寶石碎片 ${gems} 枚，寶石碎片不足。`} href="/gems" action="前往兌換" />
        <GraduateCard icon={<ShieldCheck size={22} />} title="邀友支線" text="邀友支線已開啟，可查看邀友任務與額外加碼。" href="/invite" action="前往邀友" />
        <GraduateCard icon={<HeartHandshake size={22} />} title="回穩補給金" text="正式成員可依資格送出回穩補給金申請，由後台人工審核。" href="/rescue" action="查看補給金" />
        <GraduateCard icon={<Ticket size={22} />} title="週通行證" text="週通行證完整功能即將開放，目前先保留入口。" href="/weekly-pass" action="查看預告" />
      </div>
    </section>
  );
}

function GraduateCard({ icon, title, text, href, action }: { icon: ReactNode; title: string; text: string; href: string; action: string }) {
  return <article className="graduate-activity-card"><span className="graduate-card-icon">{icon}</span><h3>{title}</h3><p>{text}</p><a className="secondary-cta" href={href}>{action}</a></article>;
}

function graduateTaskStatusLabel(status: GraduateWeekTask["status"]) {
  if (status === "completed") return "已完成";
  if (status === "available") return "可完成";
  if (status === "not_eligible") return "未達成";
  return "尚未開放";
}

function getGraduateNextGoalText(task?: GraduateWeekTask) {
  if (!task) return "本週主要任務已完成，進階任務即將開放。";
  if (task.task_code === "checkin") return "今日先完成：畢業打卡。";
  if (task.task_code === "single1000") return "下一個目標：單筆充值滿 1,000，可獲得畢業啟航紅包券。";
  return "下一個目標：累積充值滿 5,000，可獲得高級啟航紅包券。";
}

function countVoucher(rows: VoucherDetail[], type: string, status: string) {
  return rows.filter((row) => row.voucher_type === type && row.status === status).reduce((sum, row) => sum + Number(row.count ?? 0), 0);
}

function formatCoinAmount(value?: number | string | null) {
  const amount = Number(value ?? 0);
  return Number.isFinite(amount) ? amount.toLocaleString("zh-TW") : "0";
}

function regularRebateStatusText(reward: RewardRef) {
  if (reward.reward_status === "completed") return "已完成派發";
  if (reward.reward_status === "ready") return "派發中";
  if (reward.reward_status === "failed") return "派發失敗";
  if (reward.reward_status === "canceled") return "已取消";
  return "派發中";
}

function getGraduateWeekWindow(graduationAt?: string | null) {
  if (!graduationAt) return "已開啟 / 畢業時間待同步";
  const start = new Date(graduationAt);
  if (Number.isNaN(start.getTime())) return "已開啟 / 畢業時間待同步";
  const end = new Date(start.getTime() + 7 * 24 * 60 * 60 * 1000);
  const remaining = end.getTime() - Date.now();
  if (remaining <= 0) return "畢業首週期限已結束";
  const days = Math.floor(remaining / (24 * 60 * 60 * 1000));
  const hours = Math.floor((remaining % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));
  return `已開啟 / 剩餘 ${days} 天 ${hours} 小時`;
}

function getLobbyNextStep(summary: MemberSummary | null, weeklyPass: WeeklyPassStatus | null, graduateWeek: GraduateWeekStatus | null, quest: ReturnType<typeof getQuestState>) {
  if (!summary?.member) {
    return {
      title: "完成會員綁定",
      text: "請先完成會員綁定，開啟龍來喬活動任務。",
      href: "/",
      action: "前往綁定",
      primary: true
    };
  }

  const graduated = summary.member.status === "graduated" && Boolean(summary.member.graduation_at || summary.newbie_progress?.step7_status === "completed");
  if (!graduated) {
    return {
      title: "新人七關主線任務",
      text: `目前進度：${quest.progress}。請完成目前任務，解鎖下一階段獎勵。`,
      href: "/newbie",
      action: "查看新人七關",
      primary: true
    };
  }

  const weeklyPoints = weeklyPass?.points ?? summary.assets.weekly_pass_points ?? 0;
  const weeklyMax = weeklyPass?.max_points ?? 5;
  const weeklyAvailable = weeklyPass?.is_available !== false;
  const weeklyComplete = weeklyAvailable && weeklyPoints >= weeklyMax;
  const rewardText = weeklyPass?.reward_summary?.length
    ? `已獲得：${weeklyPass.reward_summary.join("、")}`
    : weeklyComplete
      ? "已獲得：寶石碎片 +1、週耀紅包券 1 張、閃耀值 +5"
      : "";

  if (!weeklyAvailable) {
    const graduateWeekEndAt = weeklyPass?.graduate_week_end_at;
    const graduateWeekEndMs = graduateWeekEndAt ? new Date(graduateWeekEndAt).getTime() : 0;
    const inGraduateWeek = graduateWeekEndMs ? Date.now() < graduateWeekEndMs : false;
    if (inGraduateWeek) {
      const graduateTasks = graduateWeek?.tasks?.length ? graduateWeek.tasks : [];
      const completedGraduateTasks = graduateTasks.filter((task) => task.status === "completed").length;
      const graduateWeekComplete = graduateTasks.length >= 3 && completedGraduateTasks >= 3;
      return {
        title: graduateWeekComplete ? "畢業首週加碼已完成" : "畢業首週啟航禮",
        text: graduateWeekComplete
          ? "你已完成畢業首週主要任務，獎勵已發放。週通行證將於畢業首週結束後的下一個週一開啟。"
          : "你已完成新人七關，畢業首週限時加碼活動已開啟。完成打卡與充值任務，可獲得寶石碎片與啟航紅包券。",
        meta: weeklyPass?.weekly_pass_available_at ? `週通行證開啟：${formatDateTime(weeklyPass.weekly_pass_available_at)}` : undefined,
        rewardText: graduateTasks.length ? `畢業首週已完成 ${completedGraduateTasks} / 3` : weeklyPass?.display_message ?? "",
        href: "/graduate-week",
        action: graduateWeekComplete ? "查看畢業首週紀錄" : "查看畢業首週",
        primary: true
      };
    }
    return {
      title: "週通行證即將開啟",
      text: "畢業首週已結束，週通行證將於下一個週一 00:00 開啟。屆時每週完成任務，可領取寶石碎片、週耀紅包券與閃耀值。",
      meta: weeklyPass?.weekly_pass_available_at ? `開啟時間：${formatDateTime(weeklyPass.weekly_pass_available_at)}｜${formatRemainingSeconds(weeklyPass.remaining_seconds)}` : undefined,
      href: "#completed-activities",
      action: "查看活動紀錄",
      primary: false
    };
  }

  if (weeklyComplete) {
    return {
      title: "本週主要任務已完成",
      text: "你已完成本週週通行證 5/5，寶石碎片、週耀紅包券與閃耀值獎勵已發放。可查看本週紀錄，或前往其他活動。",
      meta: `本週點數：${weeklyPoints} / ${weeklyMax}｜下次刷新：${weeklyPass?.remaining_text ?? "本週期間同步中"}`,
      rewardText,
      href: "/weekly-pass",
      action: "查看週通行證紀錄",
      primary: false
    };
  }

  return {
    title: "週通行證",
    text: "本週完成任務可累積通行證點數，領取寶石碎片、週耀紅包券與閃耀值。",
    meta: `本週點數：${weeklyPoints} / ${weeklyMax}`,
    rewardText: `下一個任務：${weeklyPass?.next_task?.title ?? (weeklyPoints < weeklyMax ? "每週打卡 1 次" : "本週任務已完成")}`,
    href: "/weekly-pass",
    action: "查看週通行證",
    primary: true
  };
}

function formatDateTime(value?: string | null) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("zh-TW", { hour12: false });
}

function formatRemainingSeconds(value?: number | null) {
  const seconds = Math.max(0, Number(value ?? 0));
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  if (days <= 0 && hours <= 0) return "即將開啟";
  return `剩餘 ${days} 天 ${hours} 小時`;
}

function getNextNotice(summary: MemberSummary | null) {
  if (!summary?.member) return "登入會員後即可查看活動進度與下一步任務。";
  if (summary.newbie_progress?.step7_status === "completed" || summary.member.status === "graduated") {
    return "完成畢業打卡與首週充值任務，可獲得寶石碎片與啟航紅包券。";
  }
  if (hasStep7Reward(summary)) return "畢業獎勵處理中，獎勵都入帳後才會正式畢業。";
  if (isCompleted(summary.step6_reward, summary.newbie_progress?.step6_status)) return "第 6 關已完成，第 7 關將於隔天 12:00 開啟。";
  if (isReady(summary.step6_reward, summary.newbie_progress?.step6_status)) return "第 6 關紅包獎勵已送入審核流程，等待入帳。";
  if (isCompleted(summary.step5_reward, summary.newbie_progress?.step5_status)) return "第 5 關已完成，第 6 關新人啟航紅包等待開啟。";
  if (isReady(summary.step5_reward, summary.newbie_progress?.step5_status)) return "第 5 關三存加碼已送入審核流程，等待入帳。";
  if (isCompleted(summary.step4_reward, summary.newbie_progress?.step4_status)) return "第 4 關已完成，下一步為第 5 關三存加碼。";
  if (summary.step4_reward?.reward_status === "ready") return "第 4 關入盟試煉已送入審核流程，等待入帳。";
  if (summary.step3_reward?.reward_status === "completed") return "第 3 關已完成，下一步為第 4 關入盟試煉。";
  if (summary.step3_reward?.reward_status === "ready") return "第 3 關二存加碼已送入審核流程，等待入帳。";
  if (summary.step2_reward?.reward_status === "completed") return "第 2 關已完成，下一步為第 3 關二存加碼。";
  if (summary.step2_reward?.reward_status === "ready") return "第 2 關回盟補給已送入審核流程，等待入帳。";
  if (summary.step1_reward?.reward_status === "completed") return "第 1 關已完成，下一步為第 2 關回盟補給。";
  if (summary.step1_reward?.reward_status === "ready") return "第 1 關首存 70% 已送入審核流程，等待入帳。";
  if (!summary.has_activation_deposit && summary.member.status === "channel_confirmed") return "渠道已確認，請由後台選定入盟啟動筆。";
  return "會員資料已建立，請等待後台渠道確認或入盟啟動筆設定。";
}

function getQuestState(summary: MemberSummary | null) {
  if (!summary?.member) return { progress: "尚未登入", nextStep: "登入 / 綁定會員", completedCount: 0, extraUnlocked: [] as string[] };
  const completedCount = getNewbieCompletedCount(summary);
  if (summary.newbie_progress?.step7_status === "completed" || summary.member.status === "graduated") return { progress: "新人七關已完成", nextStep: "畢業首週啟航禮", completedCount: 7, extraUnlocked: ["正式成員"] };
  if (summary.step1_reward?.reward_status === "ready") return { progress: "第 1 關待審核", nextStep: "等待客服確認後入帳", completedCount, extraUnlocked: [] as string[] };
  if (!isCompleted(summary.step1_reward)) {
    if (summary.has_activation_deposit) return { progress: "入盟啟動筆已指定", nextStep: "等待第 1 關獎勵建立", completedCount, extraUnlocked: [] as string[] };
    return { progress: "會員資料已建立", nextStep: "等待渠道確認與啟動筆設定", completedCount, extraUnlocked: [] as string[] };
  }
  if (summary.step2_reward?.reward_status === "ready") return { progress: "第 2 關待審核", nextStep: "第 2 關回盟補給待審核入帳", completedCount, extraUnlocked: ["回盟補給 10%"] };
  if (!isCompleted(summary.step2_reward)) return { progress: "第 1 關已完成", nextStep: "第 2 關回盟補給", completedCount, extraUnlocked: [] as string[] };
  if (summary.step3_reward?.reward_status === "ready") return { progress: "第 3 關待審核", nextStep: "第 3 關二存加碼待審核入帳", completedCount, extraUnlocked: ["二存加碼 80%"] };
  if (!isCompleted(summary.step3_reward)) return { progress: "第 2 關已完成", nextStep: "第 3 關二存加碼", completedCount, extraUnlocked: [] as string[] };
  if (summary.step4_reward?.reward_status === "ready") return { progress: "第 4 關待審核", nextStep: "第 4 關入盟試煉待審核入帳", completedCount, extraUnlocked: ["入盟試煉 10%"] };
  if (!isCompleted(summary.step4_reward, summary.newbie_progress?.step4_status)) return { progress: "第 3 關已完成", nextStep: "第 4 關入盟試煉", completedCount, extraUnlocked: [] as string[] };
  if (isReady(summary.step5_reward, summary.newbie_progress?.step5_status)) return { progress: "第 5 關待審核", nextStep: "第 5 關三存加碼待審核入帳", completedCount, extraUnlocked: ["三存加碼 50%"] };
  if (!isCompleted(summary.step5_reward, summary.newbie_progress?.step5_status)) return { progress: "第 4 關已完成", nextStep: "第 5 關三存加碼", completedCount, extraUnlocked: [] as string[] };
  if (isReady(summary.step6_reward, summary.newbie_progress?.step6_status)) return { progress: "第 6 關待入帳", nextStep: "第 6 關紅包獎勵待入帳", completedCount, extraUnlocked: ["新人啟航紅包"] };
  if (!isCompleted(summary.step6_reward, summary.newbie_progress?.step6_status)) return { progress: "第 5 關已完成", nextStep: "第 6 關新人啟航紅包", completedCount, extraUnlocked: ["新人啟航紅包券"] };
  if (hasStep7Reward(summary)) {
    const final10Ready = summary.step7_final10_reward?.reward_status === "ready";
    const graduationGiftCreated = Boolean(summary.step7_graduation_reward);
    return {
      progress: "第 7 關待審核",
      nextStep: final10Ready && !graduationGiftCreated ? "首存最後 10% 待審核，畢業禮尚未建立" : "等待客服確認後入帳",
      completedCount,
      extraUnlocked: final10Ready && !graduationGiftCreated ? ["首存最後 10%"] : ["首存最後 10%", "畢業禮"]
    };
  }
  if (isCompleted(summary.step6_reward, summary.newbie_progress?.step6_status)) return { progress: "第 6 關已完成", nextStep: "第 7 關等待開啟", completedCount, extraUnlocked: ["新人啟航紅包", "寶石碎片 +1"] };
  if (summary.has_activation_deposit) return { progress: "入盟啟動筆已指定", nextStep: "等待第 1 關獎勵建立", completedCount, extraUnlocked: [] as string[] };
  return { progress: "會員資料已建立", nextStep: "等待渠道確認與啟動筆設定", completedCount, extraUnlocked: [] as string[] };
}

function isCompleted(reward?: RewardRef | null, status?: string | null) {
  return reward?.reward_status === "completed" || status === "completed";
}
function isReady(reward?: RewardRef | null, status?: string | null) {
  return reward?.reward_status === "ready" || status === "ready" || status === "waiting_dispatch";
}
function hasStep7Reward(summary: MemberSummary | null) {
  return Boolean(summary?.step7_final10_reward || summary?.step7_graduation_reward || summary?.newbie_progress?.step7_status === "ready");
}

function getNewbieCompletedCount(summary: MemberSummary) {
  if (summary.newbie_progress?.step7_status === "completed" || summary.member?.status === "graduated") return 7;
  return [
    isCompleted(summary.step1_reward),
    isCompleted(summary.step2_reward),
    isCompleted(summary.step3_reward),
    isCompleted(summary.step4_reward, summary.newbie_progress?.step4_status),
    isCompleted(summary.step5_reward, summary.newbie_progress?.step5_status),
    isCompleted(summary.step6_reward, summary.newbie_progress?.step6_status),
    Boolean(summary.step7_final10_reward?.reward_status === "completed" && summary.step7_graduation_reward?.reward_status === "completed")
  ].filter(Boolean).length;
}
